import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { canSeeBookingMoney } from "@/features/bookings/booking-detail.hooks";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { attendanceOf } from "@bednbody/logic";
import type { Enums } from "@bednbody/types/database";
import { useLiveTrips } from "../live-map/live-map.hooks";

export interface OpsFilters {
  hubId: string | null; // ignored server-side for hub_manager — RLS scopes it regardless
  status: Enums<"booking_status"> | null;
  dateFrom: string | null; // yyyy-mm-dd
  dateTo: string | null;
  therapistId: string | null;
}

export const EMPTY_FILTERS: OpsFilters = {
  hubId: null,
  status: null,
  dateFrom: null,
  dateTo: null,
  therapistId: null,
};

/**
/** One booking as the ops screens read it. Money is null for a hub manager. */
export interface OpsBookingRow {
  id: string;
  ref: string;
  status: Enums<"booking_status">;
  scheduled_at: string;
  hub_id: string;
  therapist_id: string | null;
  rider_id: string | null;
  payment_method: Enums<"payment_method">;
  booking_group_id: string | null;
  /** Null for a hub manager — never selected (0285). */
  gross_amount: number | null;
  /** Null for a hub manager — never selected (0285). */
  amount_due: number | null;
  hubs: { name: string; region: string; is_pilot: boolean | null } | null;
  service_variants: {
    duration_minutes: number;
    services: { name: string } | null;
  } | null;
}

const OPS_BOOKING_COLUMNS = `id, ref, status, scheduled_at, hub_id, therapist_id, rider_id,
   payment_method, booking_group_id,
   hubs ( name, region, is_pilot ),
   service_variants ( duration_minutes, services ( name ) )`;

/**
 * Cross-hub bookings feed. We deliberately do NOT filter by role/hub client
 * side — RLS already scopes a Hub Manager's session to their own hub_id
 * (vault DATAMODEL.md §Therapist–client data wall). Requesting "all hubs" as
 * a Hub Manager just returns their own hub's rows, not an empty set or an
 * error — same query works for every role.
 *
 * ⚠ THE MONEY COLUMNS ARE THE EXCEPTION, SINCE 0285. A hub manager has no grant
 * on `gross_amount` or `amount_due`, and PostgREST answers a select naming them
 * with a 42501 for the WHOLE query — every screen on this feed would go blank,
 * not lose a column. So they are asked for only by the roles that may see them,
 * and arrive as `null` for everyone else.
 */
export function useOpsBookings(filters: OpsFilters) {
  const { claims } = useAuth();
  const withMoney = claims !== null && canSeeBookingMoney(claims.role);
  return useQuery({
    queryKey: ["ops-bookings", filters, withMoney],
    queryFn: async (): Promise<OpsBookingRow[]> => {
      let query = supabase
        .from("bookings")
        .select(
          withMoney ? `${OPS_BOOKING_COLUMNS}, gross_amount, amount_due` : OPS_BOOKING_COLUMNS,
        )
        .order("scheduled_at", { ascending: false })
        .limit(200);

      if (filters.hubId) query = query.eq("hub_id", filters.hubId);
      if (filters.status) query = query.eq("status", filters.status);
      if (filters.therapistId) query = query.eq("therapist_id", filters.therapistId);
      if (filters.dateFrom) query = query.gte("scheduled_at", filters.dateFrom);
      if (filters.dateTo) query = query.lte("scheduled_at", filters.dateTo);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((row) => {
        const r = row as unknown as Record<string, unknown>;
        return {
          ...(r as unknown as Omit<OpsBookingRow, "gross_amount" | "amount_due">),
          gross_amount: withMoney ? Number(r.gross_amount ?? 0) : null,
          amount_due: withMoney ? Number(r.amount_due ?? 0) : null,
        };
      });
    },
    staleTime: 5 * 60_000,
  });
}

export function useHubsList() {
  return useQuery({
    queryKey: ["hubs-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hubs")
        .select("id, name, region, is_pilot")
        .order("name");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useHubStaff(hubId: string | null) {
  return useQuery({
    queryKey: ["hub-staff", hubId],
    queryFn: async () => {
      if (!hubId) return [];

      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, role, active, hubs ( name )")
        .eq("hub_id", hubId)
        .order("full_name");

      if (error) throw error;
      return (data ?? []).map((s) => ({
        id: s.id,
        name: s.full_name,
        role: s.role,
        active: s.active,
        status: s.active ? "active" : "inactive",
        hubName: s.hubs?.name ?? null,
        phone: null as string | null,
      }));
    },
    staleTime: 5 * 60_000,
  });
}

const localDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const toTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" }) : null;

/**
 * One row per staff member for the given service date (default today).
 *
 * ═══ THIS READS SESSIONS, NOT `attendance` ═══
 *
 * It used to query `attendance` for a `clock_in` inside the day. That table has
 * had NO WRITER since 2026-08-22: `0098` retired the therapist clock-in RPCs
 * and `0121` the rider pair, both after the spec was checked and found to
 * contain the words "clock in" exactly zero times. Duty is a started Secured
 * Session and nothing else — TEND-008/009, RIDER-005.
 *
 * So the query returned nothing, every staff member rendered "Not clocked in",
 * and the page reported an empty hub every day for three weeks. Nothing errored;
 * an empty result from a table with no writers is indistinguishable from a hub
 * where nobody turned up.
 *
 * The two Session tables replace it. Both are SELECT-able by admin/executive/
 * superadmin and hub-scoped for `hub_manager` by their own RLS (0062, 0074), so
 * this needs no view and no new grant — the same posture as `useOpsBookings`
 * above: ask for every hub and let RLS narrow it.
 *
 * ⚠ THE DAY KEY IS `service_date`, NOT A TIMESTAMP RANGE. A therapist Session
 * runs 2:00 PM to the midnight that ENDS its service date, so half of it falls
 * in the next calendar day. Filtering on `started_at` inside a local-midnight
 * window — which is what the old code did to `clock_in` — would split every
 * session across two rows of this page.
 *
 * ⚠ AND NO LOCATION LOOKUP, DELIBERATELY. `staff_live_locations` is readable
 * here, and passing `lastSeenAt` would unlock OQ-120's `early_end` preview. It
 * is not passed, because the table holds only the CURRENT pin: on a past date
 * that pin is from this week, so every unsettled past session would preview as
 * `completed`. Omitting it downgrades to the honest three-outcome answer, which
 * `attendanceOf` handles explicitly. Settled rows are unaffected — they carry a
 * stored `outcome`, which always wins.
 */
export function useAttendance(hubId: string | null, date?: string) {
  const day = date ?? localDateStr(new Date());
  const isToday = day === localDateStr(new Date());

  return useQuery({
    queryKey: ["attendance", hubId, day],
    queryFn: async () => {
      let staffQuery = supabase.from("staff").select("id, full_name, role, hubs ( name )");
      if (hubId) staffQuery = staffQuery.eq("hub_id", hubId);

      let therapistQuery = supabase
        .from("therapist_sessions")
        .select(
          "therapist_id, state, daily_state, starts_at, ends_at, started_at, ended_at, outcome",
        )
        .eq("service_date", day);
      if (hubId) therapistQuery = therapistQuery.eq("hub_id", hubId);

      // Unsecured rider Sessions are the hub's unclaimed inventory, not a
      // person's day — they belong to the Rider Sessions page, not here.
      let riderQuery = supabase
        .from("rider_sessions")
        .select("secured_rider_id, code, state, starts_at, ends_at, started_at, ended_at, outcome")
        .eq("service_date", day)
        .not("secured_rider_id", "is", null);
      if (hubId) riderQuery = riderQuery.eq("hub_id", hubId);

      const [staffRes, therapistRes, riderRes] = await Promise.all([
        staffQuery,
        therapistQuery,
        riderQuery,
      ]);
      if (staffRes.error) throw staffRes.error;
      if (therapistRes.error) throw therapistRes.error;
      if (riderRes.error) throw riderRes.error;

      // Who's actually mid-job right now — only meaningful when viewing today.
      const currentByStaff = new Map<string, string>();
      if (isToday) {
        let bookingsQuery = supabase
          .from("bookings")
          .select("ref, therapist_id, rider_id, status")
          .in("status", ["dispatched", "en_route_to_client", "arrived", "in_session"]);
        if (hubId) bookingsQuery = bookingsQuery.eq("hub_id", hubId);
        const { data: activeBookings, error: activeError } = await bookingsQuery;
        if (activeError) throw activeError;
        for (const b of activeBookings ?? []) {
          if (b.therapist_id) currentByStaff.set(b.therapist_id, b.ref);
          if (b.rider_id) currentByStaff.set(b.rider_id, b.ref);
        }
      }

      const therapistByStaff = new Map(
        (therapistRes.data ?? []).map((s) => [s.therapist_id, s] as const),
      );
      const riderByStaff = new Map(
        (riderRes.data ?? []).map((s) => [s.secured_rider_id as string, s] as const),
      );

      // The page is a live view when it shows today and a record otherwise, so
      // "now" is the day's own end on a past date. Evaluating a finished day
      // against the wall clock would report every one of its sessions as long
      // over — true, but it would also suppress `runningLate`, which is the one
      // signal that only makes sense inside the window.
      const nowIso = isToday ? new Date().toISOString() : new Date(`${day}T23:59:59`).toISOString();

      return (staffRes.data ?? []).map((staff) => {
        const t = therapistByStaff.get(staff.id);
        const r = riderByStaff.get(staff.id);

        const attendance = t
          ? attendanceOf({
              role: "therapist",
              // `off_final` is a decision to be off; either draft is a date the
              // cycle has not finalized, which `attendanceOf` reports as its own
              // state rather than as "off". Telling a therapist holding a draft
              // that she is off is how a shift gets missed.
              secured: t.state !== "off_final",
              draft: t.state === "draft_secured" || t.state === "draft_off",
              storedState: t.daily_state,
              startsAt: t.starts_at,
              endsAt: t.ends_at,
              startedAt: t.started_at,
              endedAt: t.ended_at,
              outcome: t.outcome,
              nowIso,
            })
          : r
            ? attendanceOf({
                role: "rider",
                // He holds it — the query already excluded unsecured rows.
                secured: true,
                // `secured`, `pre_start_waiting` and `active` are derivable from
                // the clock, so they are passed as "derive it" for the same
                // reason 0100 stores only exceptions.
                storedState:
                  r.state === "secured" || r.state === "pre_start_waiting" || r.state === "active"
                    ? null
                    : r.state,
                startsAt: r.starts_at,
                endsAt: r.ends_at,
                startedAt: r.started_at,
                endedAt: r.ended_at,
                outcome: r.outcome,
                nowIso,
              })
            : null;

        return {
          id: staff.id,
          name: staff.full_name,
          role: staff.role,
          hubName: staff.hubs?.name ?? null,
          /** The rider Session code (A/B/C); therapists have one fixed period. */
          sessionCode: r?.code ?? null,
          /** Null when this staff member holds no Session for the date at all. */
          attendance,
          startsAt: t?.starts_at ?? r?.starts_at ?? null,
          endsAt: t?.ends_at ?? r?.ends_at ?? null,
          startedAtIso: t?.started_at ?? r?.started_at ?? null,
          startedTime: toTime(t?.started_at ?? r?.started_at ?? null),
          endedTime: toTime(t?.ended_at ?? r?.ended_at ?? null),
          currentBooking: currentByStaff.get(staff.id) ?? null,
        };
      });
    },
    refetchInterval: !isToday ? false : 30_000, // live only for today
  });
}

export function useCashRemittances(hubId: string | null) {
  return useQuery({
    queryKey: ["cash-remittances", hubId],
    queryFn: async () => {
      if (!hubId) return [];

      // cash_remittances (0011) has no `date` column (use created_at) and
      // staff's name column is `full_name`, not `name`. `staff` has three FKs
      // into this table (therapist_id/received_by/collected_by) so the
      // embed must name which one via the constraint.
      const { data, error } = await supabase
        .from("cash_remittances")
        .select(
          "id, therapist_id, amount, status, created_at, staff!cash_remittances_therapist_id_fkey (full_name)",
        )
        .eq("hub_id", hubId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data ?? []).map((item) => ({
        id: item.id,
        therapistName: item.staff?.full_name ?? "Unknown",
        amount: item.amount,
        status: item.status,
        date: new Date(item.created_at ?? "").toLocaleDateString("en-PH"),
      }));
    },
    enabled: !!hubId,
    staleTime: 5 * 60_000,
  });
}

// Remittance stage mutations moved to features/cash/cash.hooks.ts — the cash
// domain owns its writes; this file keeps only the hub-dashboard reads.

/**
 * hubId: pass the caller's own hub_id for a Hub Manager (they "cannot see
 * other hubs" per User Stories — Admin §Hub Manager); pass null for
 * cross-hub roles (admin/executive/superadmin) to see the whole fleet.
 */
export function useActiveRiders(hubId: string | null) {
  const staffQuery = useQuery({
    queryKey: ["active-riders", hubId],
    queryFn: async () => {
      let query = supabase
        .from("staff")
        .select("id, full_name, hubs ( name )")
        .eq("role", "rider")
        .eq("active", true);
      if (hubId) query = query.eq("hub_id", hubId);

      const { data, error } = await query;
      if (error) throw error;

      return (data ?? []).map((rider) => ({
        id: rider.id,
        hubId: hubId ?? "unknown",
        name: rider.full_name,
        hub: rider.hubs?.name ?? "Unknown",
        status: "available",
      }));
    },
    staleTime: 30_000,
  });

  // Same `dispatch/live-trips` source /live-map uses — one latest fix per
  // live trip. Only the booking ref is surfaced here; raw lat/lng stays off
  // this list on purpose (live positions belong on /live-map, not a polled
  // dashboard list — see StaffAvailabilityList.tsx and OngoingTripsCard.tsx).
  const liveTrips = useLiveTrips();

  const data = useMemo(() => {
    if (!staffQuery.data) return staffQuery.data;
    const tripByRider = new Map((liveTrips.data ?? []).map((t) => [t.riderId, t]));
    return staffQuery.data.map((rider) => ({
      ...rider,
      currentBooking: tripByRider.get(rider.id)?.bookingRef ?? null,
      location: null as { lat: number; lng: number } | null,
    }));
  }, [staffQuery.data, liveTrips.data]);

  return { ...staffQuery, data };
}

export interface DailyBookingStat {
  /** yyyy-mm-dd (local) */
  date: string;
  /** Short weekday label for the axis, e.g. "Mon". */
  day: string;
  bookings: number;
  completed: number;
  /** Σ gross_amount of non-cancelled bookings that day. Booked value, not
   * settled revenue — payment truth lives with the PayMongo webhook. */
  bookedValue: number;
  /** Σ gross_amount of completed sessions. Operational revenue, not the
   * settled-payment ledger — see the PayMongo webhook for payment truth. */
  revenue: number;
}

/** The executive performance chart can be inspected by hour for today or
 * rolled up by day for a wider operating window. */
export type BookingStatsRange = "today" | 7 | 30;

/**
 * Last 7 days of hub booking activity for the dashboard trend chart.
 * Aggregated client-side over a minimal select — fine at pilot volume
 * (one hub); revisit as a SQL aggregate view if a hub's weekly bookings
 * outgrow a single query page.
 */
export function useWeeklyBookingStats(hubId: string | null) {
  const { claims } = useAuth();
  const withMoney = claims !== null && canSeeBookingMoney(claims.role);
  return useQuery({
    queryKey: ["weekly-booking-stats", hubId, withMoney],
    queryFn: async (): Promise<DailyBookingStat[]> => {
      const days: DailyBookingStat[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0);
        days.push({
          date: d.toISOString().slice(0, 10),
          day: d.toLocaleDateString("en-PH", { weekday: "short" }),
          bookings: 0,
          completed: 0,
          bookedValue: 0,
          revenue: 0,
        });
      }

      const since = new Date();
      since.setDate(since.getDate() - 6);
      since.setHours(0, 0, 0, 0);

      // Same 0285 rule as useOpsBookings: a hub manager cannot name
      // gross_amount, so their trend counts bookings and leaves revenue at 0.
      let query = supabase
        .from("bookings")
        .select(withMoney ? "scheduled_at, status, gross_amount" : "scheduled_at, status")
        .gte("scheduled_at", since.toISOString());
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;

      const byDate = new Map(days.map((d) => [d.date, d]));
      const rows = (data ?? []) as unknown as {
        scheduled_at: string;
        status: string;
        gross_amount?: number | null;
      }[];
      for (const b of rows) {
        const key = new Date(b.scheduled_at).toISOString().slice(0, 10);
        const bucket = byDate.get(key);
        if (!bucket) continue;
        bucket.bookings += 1;
        if (b.status === "completed") {
          bucket.completed += 1;
          bucket.revenue += withMoney ? Number(b.gross_amount ?? 0) : 0;
        }
        if (b.status !== "cancelled") bucket.bookedValue += Number(b.gross_amount ?? 0);
      }
      return days;
    },
    staleTime: 60_000,
  });
}

/**
 * Performance chart data for the cross-hub command centre. Today's view
 * intentionally has one bucket per hour so a superadmin can see when demand
 * is arriving; wider windows stay at daily resolution to remain legible.
 *
 * This remains a bounded pilot-volume read. Move it to an aggregate view
 * before one selected window can exceed a single page of bookings.
 */
export function useBookingPerformanceStats(hubId: string | null, range: BookingStatsRange) {
  return useQuery({
    queryKey: ["booking-performance-stats", hubId, range],
    queryFn: async (): Promise<DailyBookingStat[]> => {
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);

      const dateKey = (date: Date) =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
          date.getDate(),
        ).padStart(2, "0")}`;

      const buckets: DailyBookingStat[] = [];
      if (range === "today") {
        for (let hour = 0; hour < 24; hour++) {
          const bucketStart = new Date(start);
          bucketStart.setHours(hour, 0, 0, 0);
          buckets.push({
            date: bucketStart.toISOString(),
            day: bucketStart.toLocaleTimeString("en-PH", {
              hour: "numeric",
              hour12: true,
            }),
            bookings: 0,
            completed: 0,
            bookedValue: 0,
            revenue: 0,
          });
        }
      } else {
        start.setDate(start.getDate() - (range - 1));
        for (let offset = 0; offset < range; offset++) {
          const bucketStart = new Date(start);
          bucketStart.setDate(start.getDate() + offset);
          buckets.push({
            date: dateKey(bucketStart),
            day: bucketStart.toLocaleDateString("en-PH", {
              month: range === 30 ? "short" : undefined,
              day: range === 30 ? "numeric" : undefined,
              weekday: range === 7 ? "short" : undefined,
            }),
            bookings: 0,
            completed: 0,
            bookedValue: 0,
            revenue: 0,
          });
        }
      }

      let query = supabase
        .from("bookings")
        .select("scheduled_at, status, gross_amount")
        .gte("scheduled_at", start.toISOString())
        .lt("scheduled_at", end.toISOString());
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;

      const byPeriod = new Map(
        buckets.map((bucket) => [
          range === "today" ? new Date(bucket.date).getHours() : bucket.date,
          bucket,
        ]),
      );
      for (const booking of data ?? []) {
        const scheduledAt = new Date(booking.scheduled_at);
        const key = range === "today" ? scheduledAt.getHours() : dateKey(scheduledAt);
        const bucket = byPeriod.get(key);
        if (!bucket) continue;

        bucket.bookings += 1;
        if (booking.status === "completed") {
          bucket.completed += 1;
          bucket.revenue += booking.gross_amount;
        }
        if (booking.status !== "cancelled") bucket.bookedValue += booking.gross_amount;
      }

      return buckets;
    },
    staleTime: 60_000,
  });
}

/**
 * No-show reports for the hub (User Stories — Admin §Hub Manager: "alerted on
 * no-show reports"). Plain RLS read: nsr_hub_read (0012) scopes hub_manager
 * to reports whose booking belongs to their hub_id. Newest first; there is no
 * resolved/dismissed column in the schema (replacement-dispatch logic is an
 * Open Item), so this shows the latest reports as-is rather than inventing a
 * lifecycle.
 */
export function useNoShowReports(hubId: string | null) {
  return useQuery({
    queryKey: ["no-show-reports", hubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("no_show_reports")
        .select("id, party, created_at, bookings ( ref, hub_id )")
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      // RLS already scopes hub_manager; the extra filter only matters for
      // admin/superadmin visiting a hub-scoped view.
      return (data ?? []).filter((r) => !hubId || r.bookings?.hub_id === hubId);
    },
    enabled: !!hubId,
    refetchInterval: 30_000,
  });
}
